import {addQueryToPath} from '../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {ScrollDirectionsEnum} from '../../../../../srcCommon/scroll/scrollerTypes';
import {CellsConversationsBoundary, CellsMessagesBoundary} from '../../../../helpers/cellsHelpers';
import {LocalPreferencesSizesEnum} from '../../../../models/api/localPreferencesModel';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {AppState} from '../../../appState';
import {getSizes} from '../../../data/localPreferences/localPreferencesSelectors';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../inboxes/parameters/inboxesParameters';
import {
  buildParametersFetchPath,
  buildParametersFetchQueryContext,
  buildParametersFetchQuerySort,
  buildParametersSearchTimezone,
} from '../../inboxes/parameters/inboxesParametersFetchHelpers';
import {findFirstSelectedId, InboxesSelectionIdType} from '../../inboxes/parameters/inboxesParametersHelpers';
import {makeCellsContext} from '../context/cellsContextHelpers';
import {getCellsSearchHighlightAlgorithm, getCellsSearchQueryHash} from '../search/cellsPanelSearchSelectors';
import {HighlightAlgorithmsEnum} from '../search/cellsPanelSearchState';

/*
 * Cells fetch.
 */

export function buildCellsPathAndQuery(
  state: AppState,
  parameters: InboxesParameters,
  canUseSearchRawEndpoint = false,
) {
  const cellsContext = makeCellsContext(state, parameters);
  const path = buildParametersFetchPath(parameters, cellsContext, canUseSearchRawEndpoint);
  if (!path) {
    return undefined;
  }

  const isAscending = parameters.sortDirection === CellsSortDirectionsEnum.ASCENDING;
  const sortOrderParameterPrefix = isAscending ? '' : '-';
  const sortOrderParameter = `${sortOrderParameterPrefix}${buildParametersFetchQuerySort(parameters)}`;

  const aroundId = findFirstSelectedId<InboxesSelectionIdType>(parameters.selection);
  const context = buildParametersFetchQueryContext(parameters, cellsContext);
  const queryHighlightLength = buildSearchHighlightLength(state, parameters);
  const filters = parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES && parameters.filters;

  const query = {
    around_id: aroundId,
    sort: sortOrderParameter,
    context,
    filters: (filters && filters.join(',')) || undefined,
    ...buildSearchParameters(state, parameters),
    ...queryHighlightLength,
  };

  return addQueryToPath(path, query);
}

const minHighlightLength = 18;
const bumpedMinHighlightLength = 22;
const maxHighlightLength = 80;
export const minCellsPanelWidth = 280;
export const maxCellsPanelWidth = 700;

export function computeSearchHighlightLength(state: AppState) {
  const cellsPanelWidth = getSizes(state)[LocalPreferencesSizesEnum.CELLS];
  const widthRatio = (cellsPanelWidth - minCellsPanelWidth) / (maxCellsPanelWidth - minCellsPanelWidth);

  const highlightAlgorithm = getCellsSearchHighlightAlgorithm(state);
  const minimumHighlightLength =
    highlightAlgorithm === HighlightAlgorithmsEnum.BUMP_HIGHLIGHT_LENGTH
      ? bumpedMinHighlightLength
      : minHighlightLength;

  const highlightLength = minimumHighlightLength + (maxHighlightLength - minimumHighlightLength) * widthRatio;

  return Math.round(highlightLength);
}

function buildSearchHighlightLength(state: AppState, parameters: InboxesParameters) {
  // If we're not in search mode, no need to add highlight length.
  if (!hasSearchInInboxesParameters(parameters)) {
    return {};
  }

  // Otherwise, compute the length to request.
  const highlightLength = computeSearchHighlightLength(state);

  return {highlight_length: highlightLength};
}

/*
 * Cells fetch more.
 */

export function buildMoreCellsPathAndQuery(
  state: AppState,
  parameters: InboxesParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsConversationsBoundary | CellsMessagesBoundary,
  canUseSearchRawEndpoint = false,
) {
  const cellsContext = makeCellsContext(state, parameters);
  const path = buildParametersFetchPath(parameters, cellsContext, canUseSearchRawEndpoint);
  if (!path) {
    return undefined;
  }

  // Build the query string.
  const isTop = direction === ScrollDirectionsEnum.TOP;
  const isDescending = parameters.sortDirection === CellsSortDirectionsEnum.DESCENDING;

  const sortPropertyParameter = isTop === isDescending ? 'after' : 'before';
  const sortOrderParameterPrefix = isTop === isDescending ? '' : '-';
  const sortOrderParameter = `${sortOrderParameterPrefix}${buildParametersFetchQuerySort(parameters)}`;
  const context = buildParametersFetchQueryContext(parameters, cellsContext);
  const highlightLengthQuery = buildSearchHighlightLength(state, parameters);
  const filters = parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES && parameters.filters;

  const query = {
    sort: sortOrderParameter,
    last_id: referenceBoundary.id,
    context,
    [sortPropertyParameter]: referenceBoundary.date,
    filters: (filters && filters.join(',')) || undefined,
    ...buildSearchParameters(state, parameters),
    ...highlightLengthQuery,
  };

  // Build the path and combine.
  return addQueryToPath(path, query);
}

/*
 * Search-related query parameters.
 */

export function buildSearchParameters(state: AppState, parameters: InboxesParameters) {
  const queryHash = getCellsSearchQueryHash(state);
  const timezone = buildParametersSearchTimezone(parameters);
  const highlightAlgorithm = getCellsSearchHighlightAlgorithm(state);

  // If the highlight algorithm is `BUMP_HIGHLIGHT_LENGTH`, replace it by `TRIM_CHARS_MULTIPLIER_3`.
  const algorithm =
    highlightAlgorithm === HighlightAlgorithmsEnum.BUMP_HIGHLIGHT_LENGTH
      ? HighlightAlgorithmsEnum.TRIM_CHARS_MULTIPLIER_3
      : highlightAlgorithm;

  return {
    query_hash: queryHash,
    timezone,
    highlight_algorithm: algorithm,
  };
}
